import type { PettyCashVoucher, TimesheetEntry, BillingInvoice, ClientCompany, ImportException, LedgerSyncImportBatch, LedgerSyncMetadata, LedgerSyncMetadataOverrides } from './ledgersync.schema'
import { DEFAULT_LEDGER_SYNC_METADATA } from './ledgersync.schema'
import { AlphalistCompiler } from './alphalist.compiler'

export interface PettyCashFundStatus {
  totalFund: number // ₱50,000 default petty cash float
  currentBalance: number
  isBelowThreshold: boolean // true if balance < ₱5,000
  thresholdAmount: number // ₱5,000
  needsReplenishment: boolean
}

export class LedgerSyncService {
  static getMetadata(overrides?: LedgerSyncMetadataOverrides): LedgerSyncMetadata {
    return {
      ...DEFAULT_LEDGER_SYNC_METADATA,
      ...overrides,
      pettyCash: { ...DEFAULT_LEDGER_SYNC_METADATA.pettyCash, ...overrides?.pettyCash },
      import: { ...DEFAULT_LEDGER_SYNC_METADATA.import, ...overrides?.import },
      compliance: { ...DEFAULT_LEDGER_SYNC_METADATA.compliance, ...overrides?.compliance },
    }
  }

  static validateClientForPosting(client: Pick<ClientCompany, 'companyName' | 'tinNumber'>, metadata?: LedgerSyncMetadataOverrides): ImportException['type'][] {
    const policy = this.getMetadata(metadata).import
    return policy.requireTinForClientPosting && (!client.tinNumber || client.tinNumber.startsWith('000-'))
      ? ['missing_or_placeholder_tin']
      : []
  }

  static canPostImport(exceptions: Pick<ImportException, 'status' | 'severity'>[], metadata?: LedgerSyncMetadataOverrides): boolean {
    const policy = this.getMetadata(metadata).import
    return !exceptions.some((exception) => exception.status === 'open' && (exception.severity === 'high' || policy.blockUnmatchedCollections && exception.severity === 'medium'))
  }

  /**
   * Creates the tenant-scoped key that repositories must store under a unique constraint
   * when a batch is posted. Tenant scoping keeps independently generated client keys apart.
   */
  static getImportPostingIdempotencyKey(tenantId: string, idempotencyKey: string): string {
    return `${tenantId.trim()}:${idempotencyKey.trim()}`
  }

  /**
   * Posting is a domain transition, not a dry-run import side effect. A repository must
   * atomically persist the returned scoped key with the posted batch to prevent races.
   */
  static canPostApprovedImport(
    batch: Pick<LedgerSyncImportBatch, 'tenantId' | 'mode'>,
    exceptions: Pick<ImportException, 'status' | 'severity'>[],
    idempotencyKey: string,
    postedIdempotencyKeys: ReadonlySet<string>,
    metadata?: LedgerSyncMetadataOverrides,
  ): boolean {
    const tenantId = batch.tenantId.trim()
    const key = idempotencyKey.trim()

    return batch.mode === 'approved'
      && tenantId.length > 0
      && key.length > 0
      && this.canPostImport(exceptions, metadata)
      && !postedIdempotencyKeys.has(this.getImportPostingIdempotencyKey(tenantId, key))
  }
  /**
   * Calculates petty cash fund balance and checks if < ₱5,000 threshold alert should trigger.
   */
  static evaluatePettyCashFund(
    vouchers: PettyCashVoucher[],
    initialFloat: number = 50000,
    threshold: number = 5000
  ): PettyCashFundStatus {
    const disbursedSum = vouchers
      .filter((v) => v.status === 'disbursed' || v.status === 'approved')
      .reduce((sum, v) => sum + v.amount, 0)

    const currentBalance = initialFloat - disbursedSum
    const isBelowThreshold = currentBalance < threshold

    return {
      totalFund: initialFloat,
      currentBalance,
      isBelowThreshold,
      thresholdAmount: threshold,
      needsReplenishment: isBelowThreshold,
    }
  }

  /**
   * Converts approved timesheets into billing invoice items.
   */
  static convertTimesheetsToInvoice(
    clientName: string,
    timesheets: TimesheetEntry[]
  ): { totalBillable: number; items: Array<{ description: string; amount: number }> } {
    const items = timesheets.map((ts) => ({
      description: `${ts.taskDescription} (${ts.hoursSpent} hrs @ ₱${ts.hourlyRate}/hr by ${ts.accountantName})`,
      amount: ts.billableAmount,
    }))

    const totalBillable = items.reduce((sum, i) => sum + i.amount, 0)
    return { totalBillable, items }
  }

  /**
   * Aggregates weekly collections for Friday 5 PM report.
   */
  static aggregateWeeklyCollections(
    invoices: BillingInvoice[],
    weekLabel: string
  ): { week: string; totalCollected: number; paidInvoiceCount: number; collectionRatePercent: number } {
    const weekInvoices = invoices.filter((i) => i.weeklyCollectionWeek === weekLabel)
    const paidInvoices = weekInvoices.filter((i) => i.status === 'paid')

    const totalCollected = paidInvoices.reduce((sum, i) => sum + i.totalAmount, 0)
    const totalBilled = weekInvoices.reduce((sum, i) => sum + i.totalAmount, 0)
    const collectionRatePercent = totalBilled > 0 ? (totalCollected / totalBilled) * 100 : 100

    return {
      week: weekLabel,
      totalCollected,
      paidInvoiceCount: paidInvoices.length,
      collectionRatePercent,
    }
  }
}
